import React, {useState} from 'react';
import './Main.css';

interface AdPackage {
  id: string;
  name: string;
  icon: string;
  price: number;
  duration: string;
  impressions: string;
  features: string[];
  popular?: boolean;
  bestFor: string;
}

const Advertise: React.FC = () => {
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [adType, setAdType] = useState<'banner' | 'sponsored' | 'featured' | 'video'>('banner');
  const [showCheckout, setShowCheckout] = useState(false);
  const pageSidePadding = 'clamp(12px, 4vw, 20px)';
  const sectionCardPadding = 'clamp(28px, 8vw, 50px)';
  const ctaSectionPadding = 'clamp(32px, 9vw, 60px) clamp(16px, 6vw, 40px)';

  const adPackages: AdPackage[] = [
    {
      id: 'starter',
      name: 'Starter',
      icon: '🚀',
      price: 99,
      duration: '7 days',
      impressions: '10,000',
      features: [
        'Banner ads on homepage',
        'Mobile & desktop display',
        'Basic analytics dashboard',
        'Audience demographics',
        'Click tracking'
      ],
      bestFor: 'Small businesses & individuals'
    },
    {
      id: 'growth',
      name: 'Growth',
      icon: '📈',
      price: 299,
      duration: '30 days',
      impressions: '50,000',
      features: [
        'Everything in Starter',
        'Sponsored posts in feed',
        'Featured service listing',
        'Advanced analytics',
        'A/B testing',
        'Retargeting options'
      ],
      popular: true,
      bestFor: 'Growing businesses'
    },
    {
      id: 'professional',
      name: 'Professional',
      icon: '💼',
      price: 799,
      duration: '90 days',
      impressions: '200,000',
      features: [
        'Everything in Growth',
        'Video ads in reels',
        'Homepage takeover (1 day)',
        'Priority placement',
        'Dedicated account manager',
        'Custom targeting',
        'Conversion tracking'
      ],
      bestFor: 'Established businesses'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      icon: '🏢',
      price: 2499,
      duration: '365 days',
      impressions: '1,000,000+',
      features: [
        'Everything in Professional',
        'Custom ad placements',
        'Brand partnership opportunities',
        'Exclusive sponsorships',
        'White-label options',
        'API access',
        'Quarterly strategy sessions',
        'Premium support'
      ],
      bestFor: 'Large enterprises & brands'
    }
  ];

  const adTypes = [
    {
      id: 'banner',
      name: 'Banner Ads',
      icon: '🖼️',
      description: 'Display ads on homepage, profile pages, and feed',
      sizes: ['728x90', '300x250', '160x600'],
      minBudget: '$99'
    },
    {
      id: 'sponsored',
      name: 'Sponsored Posts',
      icon: '📱',
      description: 'Native ads that appear in user feeds',
      sizes: ['Standard post format'],
      minBudget: '$199'
    },
    {
      id: 'featured',
      name: 'Featured Listings',
      icon: '⭐',
      description: 'Highlight your service at the top of search results',
      sizes: ['Service card format'],
      minBudget: '$149'
    },
    {
      id: 'video',
      name: 'Video Ads',
      icon: '🎬',
      description: 'Video ads in reels and stories',
      sizes: ['9:16 vertical', '16:9 horizontal'],
      minBudget: '$299'
    }
  ];

  const handlePurchase = (packageId: string) => {
    setSelectedPackage(packageId);
    setShowCheckout(true);
  };

  const handleCheckout = () => {
    const pkg = adPackages.find(p => p.id === selectedPackage);
    alert(`Processing payment for ${pkg?.name} package...\n\nIn production, this would:\n1. Redirect to Stripe checkout\n2. Process payment securely\n3. Create ad campaign\n4. Provide ad upload interface\n5. Start campaign immediately`);
    setShowCheckout(false);
  };

  return (
    <div style={{background: '#f8f9fa', paddingBottom: '40px'}}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: `80px ${pageSidePadding} 60px`,
        marginBottom: '0'
      }}>
        <div className="container" style={{maxWidth: 'none', margin: 0, textAlign: 'center', padding: '0'}}>
          <h1 style={{
            margin: '0 0 20px 0',
            fontSize: '48px',
            fontWeight: '900',
            textShadow: '2px 2px 8px rgba(0,0,0,0.3)'
          }}>
            Advertise on XPSR 📢
          </h1>
          <p style={{
            fontSize: '22px',
            opacity: 0.95,
            maxWidth: '800px',
            margin: '0 auto 30px'
          }}>
            Reach thousands of talented creators, athletes, and professionals. Grow your business with targeted advertising.
          </p>

          {/* Stats */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '60px',
            marginTop: '40px',
            flexWrap: 'wrap'
          }}>
            <div>
              <div style={{fontSize: '36px', fontWeight: '900', marginBottom: '5px'}}>500K+</div>
              <div style={{fontSize: '16px', opacity: 0.9}}>Monthly Users</div>
            </div>
            <div>
              <div style={{fontSize: '36px', fontWeight: '900', marginBottom: '5px'}}>2M+</div>
              <div style={{fontSize: '16px', opacity: 0.9}}>Monthly Impressions</div>
            </div>
            <div>
              <div style={{fontSize: '36px', fontWeight: '900', marginBottom: '5px'}}>85%</div>
              <div style={{fontSize: '16px', opacity: 0.9}}>Engagement Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Ad Types Section */}
      <div style={{background: 'white', padding: `60px ${pageSidePadding}`, marginBottom: '40px'}}>
        <div className="container" style={{maxWidth: 'none', margin: 0, padding: '0'}}>
          <h2 style={{textAlign: 'center', fontSize: '36px', marginBottom: '15px', fontWeight: '900'}}>
            Choose Your Ad Format
          </h2>
          <p style={{textAlign: 'center', color: '#666', fontSize: '18px', marginBottom: '40px'}}>
            Select the format that best fits your campaign goals
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: '24px'
          }}>
            {adTypes.map(type => (
              <div
                key={type.id}
                onClick={() => setAdType(type.id as any)}
                className="card"
                style={{
                  padding: '30px',
                  cursor: 'pointer',
                  border: adType === type.id ? '3px solid #667eea' : '1px solid #e0e0e0',
                  transition: 'all 0.3s',
                  background: adType === type.id ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)' : 'white'
                }}
                onMouseEnter={(e) => {
                  if (adType !== type.id) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                <div style={{fontSize: '48px', marginBottom: '15px'}}>{type.icon}</div>
                <h3 style={{fontSize: '22px', marginBottom: '10px', fontWeight: '700'}}>{type.name}</h3>
                <p style={{color: '#666', fontSize: '15px', marginBottom: '15px', lineHeight: '1.6'}}>
                  {type.description}
                </p>
                <div style={{fontSize: '13px', color: '#666', marginBottom: '8px'}}>
                  <strong>Sizes:</strong> {type.sizes.join(', ')}
                </div>
                <div style={{fontSize: '14px', color: '#667eea', fontWeight: '700'}}>
                  Starting at {type.minBudget}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Packages */}
      <div className="container" style={{maxWidth: 'none', margin: 0, padding: `0 ${pageSidePadding}`}}>
        <h2 style={{textAlign: 'center', fontSize: '36px', marginBottom: '15px', fontWeight: '900'}}>
          Advertising Packages
        </h2>
        <p style={{textAlign: 'center', color: '#666', fontSize: '18px', marginBottom: '50px'}}>
          Choose the package that fits your budget and goals
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
          gap: '30px',
          marginBottom: '60px'
        }}>
          {adPackages.map(pkg => (
            <div
              key={pkg.id}
              className="card"
              style={{
                padding: '0',
                overflow: 'hidden',
                border: pkg.popular ? '3px solid #667eea' : '1px solid #e0e0e0',
                position: 'relative',
                transition: 'transform 0.3s, box-shadow 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              {pkg.popular && (
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '-35px',
                  background: '#ffc107',
                  color: '#000',
                  padding: '5px 40px',
                  fontSize: '12px',
                  fontWeight: '700',
                  transform: 'rotate(45deg)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  POPULAR
                </div>
              )}

              <div style={{
                background: pkg.popular 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : '#f8f9fa',
                padding: '40px 30px',
                textAlign: 'center'
              }}>
                <div style={{fontSize: '64px', marginBottom: '15px'}}>{pkg.icon}</div>
                <h3 style={{
                  fontSize: '28px',
                  fontWeight: '900',
                  marginBottom: '10px',
                  color: pkg.popular ? 'white' : '#333'
                }}>
                  {pkg.name}
                </h3>
                <div style={{
                  fontSize: '48px',
                  fontWeight: '900',
                  marginBottom: '5px',
                  color: pkg.popular ? 'white' : '#667eea'
                }}>
                  ${pkg.price}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: pkg.popular ? 'rgba(255,255,255,0.9)' : '#666',
                  marginBottom: '10px'
                }}>
                  {pkg.duration} campaign
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: pkg.popular ? 'white' : '#333'
                }}>
                  {pkg.impressions} impressions
                </div>
              </div>

              <div style={{padding: '30px'}}>
                <div style={{
                  fontSize: '13px',
                  color: '#667eea',
                  fontWeight: '700',
                  marginBottom: '15px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Best for: {pkg.bestFor}
                </div>

                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0 0 30px 0'
                }}>
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} style={{
                      padding: '10px 0',
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: '14px',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <span style={{color: '#28a745', fontSize: '16px'}}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePurchase(pkg.id)}
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: '12px',
                    border: 'none',
                    background: pkg.popular 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : '#667eea',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  Get Started
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Why Advertise Section */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: sectionCardPadding,
          marginBottom: '60px'
        }}>
          <h2 style={{textAlign: 'center', fontSize: '32px', marginBottom: '40px', fontWeight: '900'}}>
            Why Advertise on XPSR?
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))',
            gap: '40px'
          }}>
            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: '48px', marginBottom: '15px'}}>🎯</div>
              <h3 style={{fontSize: '20px', marginBottom: '10px', fontWeight: '700'}}>Targeted Audience</h3>
              <p style={{color: '#666', fontSize: '15px', lineHeight: '1.6'}}>
                Reach creators, athletes, artists, and professionals actively seeking services and opportunities.
              </p>
            </div>

            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: '48px', marginBottom: '15px'}}>📊</div>
              <h3 style={{fontSize: '20px', marginBottom: '10px', fontWeight: '700'}}>Real-Time Analytics</h3>
              <p style={{color: '#666', fontSize: '15px', lineHeight: '1.6'}}>
                Track impressions, clicks, conversions, and ROI with our comprehensive analytics dashboard.
              </p>
            </div>

            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: '48px', marginBottom: '15px'}}>💰</div>
              <h3 style={{fontSize: '20px', marginBottom: '10px', fontWeight: '700'}}>Cost-Effective</h3>
              <p style={{color: '#666', fontSize: '15px', lineHeight: '1.6'}}>
                Flexible pricing options that fit any budget. Pay only for what you need, when you need it.
              </p>
            </div>

            <div style={{textAlign: 'center'}}>
              <div style={{fontSize: '48px', marginBottom: '15px'}}>🚀</div>
              <h3 style={{fontSize: '20px', marginBottom: '10px', fontWeight: '700'}}>Quick Setup</h3>
              <p style={{color: '#666', fontSize: '15px', lineHeight: '1.6'}}>
                Launch your campaign in minutes with our self-service platform. No approval delays.
              </p>
            </div>
          </div>
        </div>

        {/* Custom Solutions */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          padding: ctaSectionPadding,
          textAlign: 'center',
          color: 'white'
        }}>
          <h2 style={{fontSize: '36px', marginBottom: '20px', fontWeight: '900'}}>
            Need a Custom Solution?
          </h2>
          <p style={{fontSize: '18px', marginBottom: '30px', opacity: 0.95, maxWidth: '700px', margin: '0 auto 30px'}}>
            For enterprise clients, we offer custom advertising solutions, brand partnerships, and exclusive sponsorship opportunities.
          </p>
          <button
            onClick={() => alert('Opening contact form...\n\nIn production, this would open a contact form or start a chat with the sales team.')}
            style={{
              padding: '16px 40px',
              borderRadius: '12px',
              border: '2px solid white',
              background: 'white',
              color: '#667eea',
              fontSize: '18px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Contact Sales Team
          </button>
        </div>
      </div>

      {/* Checkout Modal */}
      {showCheckout && selectedPackage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}
        onClick={() => setShowCheckout(false)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            padding: '40px',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setShowCheckout(false)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>

            <h2 style={{fontSize: '28px', marginBottom: '20px', fontWeight: '900'}}>
              Complete Your Purchase
            </h2>

            {(() => {
              const pkg = adPackages.find(p => p.id === selectedPackage);
              return (
                <>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '12px',
                    marginBottom: '30px'
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                      <span style={{fontWeight: '600'}}>Package:</span>
                      <span>{pkg?.name}</span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                      <span style={{fontWeight: '600'}}>Duration:</span>
                      <span>{pkg?.duration}</span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                      <span style={{fontWeight: '600'}}>Impressions:</span>
                      <span>{pkg?.impressions}</span>
                    </div>
                    <div style={{display: 'flex', justifyContent: 'space-between', paddingTop: '15px', borderTop: '2px solid #e0e0e0', marginTop: '15px'}}>
                      <span style={{fontWeight: '700', fontSize: '18px'}}>Total:</span>
                      <span style={{fontWeight: '900', fontSize: '24px', color: '#667eea'}}>${pkg?.price}</span>
                    </div>
                  </div>

                  <button
                    onClick={handleCheckout}
                    style={{
                      width: '100%',
                      padding: '16px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      fontSize: '18px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                      marginBottom: '15px'
                    }}
                  >
                    Proceed to Payment
                  </button>

                  <p style={{textAlign: 'center', fontSize: '13px', color: '#666'}}>
                    Secure payment powered by Stripe
                  </p>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Advertise;
